import { motion, AnimatePresence } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Quote, Star, ArrowLeft, ArrowRight } from 'lucide-react';

interface Testimonial {
  name: string;
  role: string;
  company: string;
  avatar: string;
  rating: number;
  text: string;
  results: string[];
}

interface PremiumTestimonialsProps {
  testimonials?: Testimonial[];
  className?: string;
}

const defaultTestimonials: Testimonial[] = [
  {
    name: "Marcus Thompson",
    role: "Owner, Thompson HVAC",
    company: "Thompson HVAC",
    avatar: "",
    rating: 5,
    text: "Before Xentic, we were losing about 60% of our after-hours leads. Now the chatbot picks up every single inquiry, qualifies them, and books the service call. It paid for itself in the first three days.",
    results: ["+58% leads captured", "3-day payback", "24/7 coverage"]
  },
  {
    name: "Rachel Kim",
    role: "Practice Manager, BrightSmile Dental",
    company: "BrightSmile Dental",
    avatar: "",
    rating: 5,
    text: "Our front desk was overwhelmed with appointment requests and basic questions. The AI handles all of that now, and patients actually prefer it because they get instant responses at any hour.",
    results: ["+45% bookings", "Zero wait time", "Happier patients"]
  },
  {
    name: "David Alvarez",
    role: "Managing Partner, Alvarez Law Group",
    company: "Alvarez Law Group",
    avatar: "",
    rating: 5,
    text: "We only want to talk to people who are ready to hire. The chatbot screens every lead, asks the right questions, and only sends us qualified prospects. Our conversion rate went through the roof.",
    results: ["+62% qualified leads", "Smarter screening", "Higher close rate"]
  },
  {
    name: "Sarah Mitchell",
    role: "Real Estate Agent, Keller Williams",
    company: "Keller Williams",
    avatar: "https://images.unsplash.com/photo-1580489944761-15a19d654956?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    text: "Weekends and evenings used to be dead zones for lead capture. Now my AI assistant books showings around the clock. My pipeline has never been this full, and I did not have to hire anyone.",
    results: ["Weekend leads captured", "Auto-booked showings", "No extra staff"]
  },
  {
    name: "James Liu",
    role: "Founder, UrbanCart E-Commerce",
    company: "UrbanCart",
    avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
    rating: 5,
    text: "Support tickets dropped by 70% after we set up the chatbot. It handles returns, order tracking, and product questions without breaking a sweat. Our team finally has time to focus on growth.",
    results: ["-70% support tickets", "Instant answers", "Team freed up"]
  }
];

export function PremiumTestimonials({ testimonials = defaultTestimonials, className }: PremiumTestimonialsProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setDirection(1);
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [testimonials.length]);

  const slideVariants = {
    enter: (dir: number) => ({
      x: dir > 0 ? 600 : -600,
      opacity: 0,
      scale: 0.9,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
      scale: 1,
    },
    exit: (dir: number) => ({
      zIndex: 0,
      x: dir < 0 ? 600 : -600,
      opacity: 0,
      scale: 0.9,
    })
  };

  const nextTestimonial = () => {
    setDirection(1);
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setDirection(-1);
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  const current = testimonials[currentIndex];

  return (
    <section className={`py-20 relative overflow-hidden ${className || ''}`}>
      {/* Background effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 -left-32 w-80 h-80 bg-primary/10 rounded-full blur-[120px] animate-blob" />
        <div className="absolute bottom-1/4 -right-32 w-80 h-80 bg-accent/10 rounded-full blur-[120px] animate-blob-delay" />
      </div>

      <div className="container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <p className="text-sm uppercase tracking-widest text-muted-foreground mb-3">Client Success Stories</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">
            Real Results from{' '}
            <span className="text-gradient">Real Businesses</span>
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            Small businesses across multiple industries trust Xentic Solutions to capture more leads and close more deals, without adding staff.
          </p>
        </motion.div>

        {/* Main testimonial card */}
        <div className="max-w-3xl mx-auto">
          <div className="relative min-h-[340px] md:min-h-[300px]">
            <AnimatePresence initial={false} custom={direction} mode="wait">
              <motion.div
                key={currentIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ type: "spring", stiffness: 250, damping: 30 }}
                className="absolute inset-0"
              >
                <div className="glass-card rounded-2xl p-6 md:p-8 h-full relative overflow-hidden">
                  {/* Subtle gradient accent */}
                  <div
                    className="absolute top-0 right-0 w-32 h-32 rounded-bl-full opacity-20"
                    style={{ background: "linear-gradient(135deg, hsl(189 100% 50%), hsl(243 76% 59%))" }}
                  />

                  <Quote className="absolute top-5 right-5 h-8 w-8 text-primary/20" />

                  <div className="relative z-10 flex flex-col h-full">
                    {/* User info */}
                    <div className="flex items-center gap-4 mb-5">
                      <div className="relative">
                        <img
                          src={current.avatar}
                          alt={current.name}
                          className="w-14 h-14 rounded-full object-cover border-2 border-primary/30"
                        />
                        <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 rounded-full bg-green-500 border-2 border-background" />
                      </div>
                      <div>
                        <h3 className="font-heading font-bold text-foreground">{current.name}</h3>
                        <p className="text-sm text-muted-foreground">{current.role}</p>
                        <div className="flex gap-0.5 mt-1">
                          {[...Array(current.rating)].map((_, i) => (
                            <Star key={i} className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Quote */}
                    <p className="text-sm md:text-base text-muted-foreground leading-relaxed mb-5 flex-1">
                      "{current.text}"
                    </p>

                    {/* Results */}
                    <div className="flex flex-wrap gap-2">
                      {current.results.map((result, i) => (
                        <span
                          key={i}
                          className="text-xs font-medium bg-primary/10 text-primary px-3 py-1.5 rounded-full border border-primary/20"
                        >
                          {result}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-center gap-4 mt-6">
            <motion.button
              onClick={prevTestimonial}
              className="w-10 h-10 rounded-full border border-border bg-secondary/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ArrowLeft className="h-4 w-4" />
            </motion.button>

            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <motion.button
                  key={index}
                  onClick={() => {
                    setDirection(index > currentIndex ? 1 : -1);
                    setCurrentIndex(index);
                  }}
                  className={`w-2.5 h-2.5 rounded-full transition-all ${
                    index === currentIndex
                      ? 'bg-primary scale-125'
                      : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'
                  }`}
                  whileHover={{ scale: 1.3 }}
                  whileTap={{ scale: 0.9 }}
                />
              ))}
            </div>

            <motion.button
              onClick={nextTestimonial}
              className="w-10 h-10 rounded-full border border-border bg-secondary/50 flex items-center justify-center text-muted-foreground hover:text-foreground hover:border-primary/40 transition-colors"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
            >
              <ArrowRight className="h-4 w-4" />
            </motion.button>
          </div>
        </div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-14 max-w-3xl mx-auto"
        >
          {[
            { number: "40-60%", label: "More Leads Captured" },
            { number: "72hrs", label: "Average Setup Time" },
            { number: "$150", label: "One-Time Setup Cost" },
            { number: "30 Days", label: "Risk-Free Guarantee" }
          ].map((stat, index) => (
            <div key={index} className="glass-card rounded-xl p-4 text-center">
              <div className="font-heading font-bold text-xl text-primary">{stat.number}</div>
              <div className="text-xs text-muted-foreground mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
